import axios from 'axios';
import sampleMenuUiConfig from '@/mocks/sampleMenuUiConfig.json';

/** 10 minutes — matches generous Kotlin → Python LLM read timeout. */
const LLM_MENU_CONFIG_TIMEOUT_MS = 600000;

/**
 * Ask the backend (Kotlin → LLM) for structured menu configuration from SQL.
 *
 * Response `data` may include: `sql`, `analysis_summary`, `viz_suggestions`, `menu_name`,
 * `screen_title`, `parent_menu`, `x_axis_name`, `y_axis_name`, `x_axis_tooltip`, `y_axis_tooltip`,
 * `chart_mappings` (array for table + bar; expected on every response),
 * `axis_column_map` (object for bar/pie/line roles; expected alongside chart_mappings).
 *
 * @param {string} sql
 * @param {{ vizTypeFunction?: string, vizParams?: Record<string, string> }} [opts]
 */
export function generateMenuConfigFromSql(sql, opts = {}) {
  const vizParams =
    opts.vizParams && typeof opts.vizParams === 'object' ? opts.vizParams : {};
  return axios.post(
    '/api/ai/generate-menu-config',
    {
      sql: sql || '',
      vizTypeFunction: opts.vizTypeFunction || '',
      vizParams,
    },
    {
      timeout: LLM_MENU_CONFIG_TIMEOUT_MS,
      headers: { 'Content-Type': 'application/json' },
    },
  );
}

/**
 * Matches Kotlin `DEFAULT_MENU_UI_SYSTEM_PROMPT` — LLM must use only
 * `generate_forewarn_viz_config` (see `tools_hint` on the backend).
 */
/** Must match Kotlin `DEFAULT_MENU_UI_SYSTEM_PROMPT`. */
export const GENERATE_UI_SYSTEM_PROMPT =
  'Always use only the tools available to you. Use only generate_forewarn_viz_config tool';

/**
 * Progressive UI: POST /api/ai/generate-menu-ui → Kotlin forwards to Python LLM with
 * structured user payload + tools_hint: ["generate_forewarn_viz_config"].
 *
 * Expected response (caller may normalize / strip tenant): config_version 2.0 + screen (+ optional validation/analysis).
 *
 * @param {string[]} sqls
 * @param {{
 *   userPrompt?: string,
 *   requestedChartType?: string,
 *   systemPrompt?: string,
 * }} [opts]
 */
/**
 * TEMPORARY dev helper: returns the bundled sample menu-UI JSON without hitting
 * the LLM. Callers should only use this when both `/api/ai/generate-menu-ui`
 * and the per-SQL fallback are unreachable so the UI stays demo-able offline.
 *
 * Delete this once the backend endpoint is live.
 */
export function getSampleMenuUiConfig() {
  return JSON.parse(JSON.stringify(sampleMenuUiConfig));
}

export function generateMenuUiFromSqls(sqls, opts = {}) {
  const list = Array.isArray(sqls)
    ? sqls.map((s) => String(s || '').trim()).filter(Boolean)
    : [];
  const systemPrompt =
    opts.systemPrompt !== undefined && opts.systemPrompt !== null && opts.systemPrompt !== ''
      ? String(opts.systemPrompt)
      : GENERATE_UI_SYSTEM_PROMPT;
  const body = {
    sqls: list,
    userPrompt: opts.userPrompt != null ? String(opts.userPrompt) : '',
    requestedChartType:
      opts.requestedChartType != null ? String(opts.requestedChartType) : '',
    systemPrompt,
  };
  const uid =
    opts.llmUserId != null && String(opts.llmUserId).trim() !== ''
      ? String(opts.llmUserId).trim()
      : '';
  if (uid) body.llmUserId = uid;
  if (opts.llmApiKey != null && String(opts.llmApiKey).trim() !== '') {
    body.llmApiKey = String(opts.llmApiKey).trim();
  }
  if (opts.llmModel != null && String(opts.llmModel).trim() !== '') {
    body.llmModel = String(opts.llmModel).trim();
  }
  return axios.post(
    '/api/ai/generate-menu-ui',
    body,
    {
      timeout: LLM_MENU_CONFIG_TIMEOUT_MS,
      headers: { 'Content-Type': 'application/json' },
    },
  );
}
